import {getFormattedDateTime} from "@ots-frontend/ots-data";
import type {OtsBadgeTone} from "../components/OtsBadge";
import type {GaugeQuantityAudit, GaugeQuantityStatus} from "./gauge-quantity-types";

const statuses: Record<string, {label: string; tone: OtsBadgeTone}> = {
  SUCCESS: {label: "Calculated", tone: "success"},
  PARTIAL: {label: "Partial", tone: "warning"},
  FAILED: {label: "Not calculated", tone: "danger"},
  NO_GAUGES: {label: "No gauges", tone: "neutral"},
};

export const gaugeQuantityStatus = (status: GaugeQuantityStatus) =>
  statuses[status] ?? {label: status, tone: "neutral" as OtsBadgeTone};

export const formatGaugeQuantity = (value: number | null | undefined, unit?: string | null) =>
  value == null || !Number.isFinite(value) ? "Not available"
    : `${value.toLocaleString(undefined, {maximumFractionDigits: 3})}${unit ? ` ${unit}` : ""}`;

export type GaugeQuantityComparison = {kind: "match" | "differs" | "unavailable"; label: string; tone: OtsBadgeTone};

/** Compares the calculated quantity with the quantity stored on the ticket after that run. */
export const compareGaugeQuantity = (audit: Pick<GaugeQuantityAudit, "result" | "storedQuantity">): GaugeQuantityComparison => {
  const calculated = audit.result.quantity;
  const stored = audit.storedQuantity;
  const unit = audit.result.quantityUnit;
  if (calculated == null && stored == null) return {kind: "unavailable", label: "Neither has a quantity", tone: "neutral"};
  if (calculated == null) return {kind: "differs", label: `Ticket has ${formatGaugeQuantity(stored, unit)}`, tone: "warning"};
  if (stored == null) return {kind: "differs", label: "Ticket has no quantity", tone: "warning"};
  const difference = Math.round((calculated - stored) * 1000) / 1000;
  return difference === 0 ? {kind: "match", label: "Matches ticket", tone: "success"}
    : {kind: "differs", label: `${difference > 0 ? "+" : "−"}${formatGaugeQuantity(Math.abs(difference), unit)} vs ticket`, tone: "warning"};
};

export const formatAuditTime = (epochMillis: number) =>
  getFormattedDateTime(epochMillis);
